import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchOneServiceType, fetchServiceImage } from '../../services/Service';
import { Service, ServiceAddOn } from '../../types/Service';
import { getAssetById } from '../../services/Asset';
import { Button, Checkbox, Icon, Message } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { createOrder, updateOrder, fetchOrderImageUploadUrl } from '../../services/OrderStatus';
import _ from 'lodash';
import sendToast from '../../../utils/Toast';
import { uploadToSignedUrl } from '../../../services/imageUpload';
import { Document } from '../../types/Document';
import { resolveServiceDocuments } from '../../services/Documents';
import { getAttachedFileLink } from '../../utils/document';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { isPaymentRequiredForService } from '../../utils/paymentStatus';
import { CardGateReason, getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';
import ChargeBreakdown from '../ChargeBreakdown';
import useRequestChargePreview from '../../hooks/useRequestChargePreview';

// A bare domain like "google.com" resolves relative to the app when used as an
// href, turning into an in-app sub-route instead of navigating out to the site.
const normalizeUrl = (url: string): string =>
    /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;

interface CreateOrderStepThreeProps {
    FacilityId: string;
    selectedServiceId: string;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
    setStepForm: React.Dispatch<React.SetStateAction<number>>;
    selectedResidentId: string;
    selectedServiceCategoryName: string;
    setSelectedServiceId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedServiceCategoryName: React.Dispatch<React.SetStateAction<string>>;
    refreshHandler: () => void;
    facilityPaymentsEnabled?: boolean;
}

// Request details step for the order-status "Create Order" wizard modal — mirrors
// StepThree's data-fetch/submit logic but lays the fields out to match the
// order-status-page.html "New Request" modal (summary card, add-on checklist,
// name/description fields, Now/Schedule toggle) instead of the full-page 2-column layout.
const CreateOrderStepThree: React.FC<CreateOrderStepThreeProps> = ({
    FacilityId, selectedServiceId, setIsFetching, setStepForm, selectedResidentId,
    selectedServiceCategoryName, setSelectedServiceId, setSelectedServiceCategoryName, refreshHandler,
    facilityPaymentsEnabled = false,
}) => {
    const history = useHistory();
    const [selectedService, setSelectedService] = useState<Service>({} as Service);
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh: refreshCardEligibility,
    } = useResidentOrderCardEligibility({
        residentId: selectedResidentId,
        facilityId: FacilityId,
        enabled: facilityPaymentsEnabled,
    });
    // If the facility settings could not load, payments are treated as on and the gate
    // shows could-not-verify until a retry succeeds.
    const { facilityLookupFailed, retry: retryFacilityPayments } = useNeighborsPaymentAccess();
    const [serviceImage, setServiceImages] = useState<string>("");
    const [assetName, setAssetName] = useState<string>("");
    const [assetUrl, setAssetUrl] = useState<string>("");
    const [serviceDocs, setServiceDocs] = useState<Document[]>([]);
    const [loadingDocs, setLoadingDocs] = useState<boolean>(true);
    const [serviceAddOns, setServiceAddOns] = useState<ServiceAddOn[]>([]);
    const [externalCost, setExternalCost] = useState<number>(0);
    const [orderName, setOrderName] = useState<string>('');
    const [selectedServiceAddOns, setSelectedServiceAddOns] = useState<ServiceAddOn[]>([]);
    const [description, setDescription] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [nowRequest, setNowRequest] = useState<boolean>(true);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    // Guards against a second submit firing while the first is still in flight -- the
    // parent modal's Dimmer already blocks a second click, but this form also submits on
    // Enter from any text field, which bypasses the Dimmer entirely (it blocks pointer
    // hit-testing, not focused-element keyboard events). A ref (not state) so the check is
    // synchronous and can't race a pending re-render.
    const isSubmittingRef = useRef(false);

    useEffect(() => {
        const requiredAddons = serviceAddOns.filter((addon) => addon.required);
        const selectedAddons = requiredAddons.map((addon) => ({
            id: addon.id,
            itemName: addon.itemName,
            itemCost: addon.itemCost,
            required: addon.required,
        }));
        setSelectedServiceAddOns(selectedAddons);
    }, [serviceAddOns]);

    useEffect(() => {
        const fetchService = async () => {
            setIsFetching(true);
            try {
                if (selectedResidentId && selectedServiceCategoryName && selectedServiceId) {
                    const serviceRes = await fetchOneServiceType(selectedServiceId, FacilityId, selectedResidentId);
                    const fetchedService = serviceRes?.Result?.Content?.Service;
                    if (!fetchedService) {
                        throw new Error('Service is not available for this resident');
                    }

                    if (fetchedService.image) {
                        const serviceImageUrl = await fetchServiceImage({
                            serviceId: fetchedService._id,
                            Facility: FacilityId,
                            fileName: fetchedService.image,
                        });
                        setServiceImages(serviceImageUrl);
                    } else {
                        setServiceImages('');
                    }

                    if (fetchedService.Asset) {
                        try {
                            const assetRes = await getAssetById({
                                Facility: FacilityId,
                                AssetId: fetchedService.Asset,
                            });
                            if (assetRes && assetRes.response) {
                                setAssetName(assetRes.response.AssetName || '');
                                setAssetUrl(assetRes.response.URL || assetRes.response.SignedUrl || '');
                            } else {
                                setAssetName('');
                                setAssetUrl('');
                            }
                        } catch (assetError) {
                            setAssetName('');
                            setAssetUrl('');
                            sendToast('error', assetError instanceof Error ? assetError.message : 'An error occurred while fetching asset details');
                        }
                    } else {
                        setAssetName('');
                        setAssetUrl('');
                    }

                    setServiceAddOns(fetchedService.ServiceAddOns || []);
                    setExternalCost((fetchedService && fetchedService.defaults && fetchedService.defaults.externalCost) || 0);
                    setSelectedService(fetchedService);
                }
            }
            catch (error) {
                setSelectedService({} as Service);
                setServiceAddOns([]);
                setExternalCost(0);
                setServiceImages('');
                setAssetName('');
                setAssetUrl('');
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching services');
            }
            finally {
                setIsFetching(false);
            }
        };
        fetchService();
    }, [selectedServiceId, selectedResidentId, selectedServiceCategoryName, FacilityId]);

    useEffect(() => {
        const fetchServiceDocs = async () => {
            try {
                setLoadingDocs(true);
                const docs = await resolveServiceDocuments(FacilityId, selectedService);
                setServiceDocs(docs);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching service document');
                setServiceDocs([]);
            }
            finally {
                setLoadingDocs(false);
            }
        };

        fetchServiceDocs();
    }, [selectedService?.DocIds, FacilityId]);

    const handleServiceAddons = (id: string, value: boolean) => {
        let updatedAddOns = _.cloneDeep(selectedServiceAddOns);
        if (value) {
            serviceAddOns.forEach(((addon) => {
                if (addon.id === id) {
                    updatedAddOns.push(addon);
                }
            }));
        } else {
            updatedAddOns = updatedAddOns.filter((addon: ServiceAddOn) => addon.id !== id);
        }
        setSelectedServiceAddOns(updatedAddOns);
    };

    const selectScheduleNow = () => {
        setStartDate('');
        setNowRequest(true);
    };

    const selectScheduleLater = () => {
        setNowRequest(false);
        if (!startDate) {
            setStartDate(momentTZ().format('YYYY-MM-DDTHH:mm:ss'));
        }
    };

    const totalCostForOrder: number =
        selectedServiceAddOns
            .reduce((acc, addon) => acc + Number(addon.itemCost), 0) +
        Number(externalCost);

    const paymentRequired = facilityPaymentsEnabled && isPaymentRequiredForService(selectedService);
    const cardGateReason: CardGateReason = paymentRequired
        ? resolveCardGateReason({ loading: cardEligibilityLoading, lookupFailed: lookupFailed || facilityLookupFailed, hasDefaultCard, hasAnyCard, defaultCardExpired })
        : null;

    const { shownChargePreview, chargePreviewLoading, resolvePaymentConsent } = useRequestChargePreview({
        paymentRequired,
        facilityId: FacilityId,
        residentId: selectedResidentId,
        serviceId: selectedService && selectedService._id ? String(selectedService._id) : '',
        addOnIds: selectedServiceAddOns.map((addOn) => String(addOn.id)),
    });

    const canSubmit = Boolean(orderName.trim() && (nowRequest || startDate) && !cardGateReason);

    const handleRetryCardEligibility = async () => {
        if (facilityLookupFailed) retryFacilityPayments();
        await refreshCardEligibility();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        try {
            setIsFetching(true);
            if (!selectedService || !selectedService._id) {
                throw new Error('Please select a valid service');
            }
            if (cardGateReason) {
                throw new Error(getCardGateMessage(cardGateReason));
            }
            if (!nowRequest && !moment(startDate).isValid()) {
                throw new Error('Please select a valid end date');
            }
            if (!selectedResidentId || !selectedServiceCategoryName || !selectedServiceId || !orderName.trim() || (!nowRequest && !startDate)) {

                const errors: string[] = [];

                if (!selectedResidentId) {
                    errors.push("Please select a resident");
                }
                if (!selectedServiceCategoryName) {
                    errors.push("Please select a service category");
                }
                if (!selectedServiceId) {
                    errors.push("Please select a service");
                }
                if (!orderName.trim()) {
                    errors.push("Order name cannot be empty");
                }
                if (!startDate && !nowRequest) {
                    errors.push("Please select start date");
                }
                if (errors.length > 0) {
                    const errorMessage = errors.join('\n,');
                    throw new Error(errorMessage);
                }
            }
            const facilityId = FacilityId;
            const startDateMomentObj = momentTZ(startDate);
            const endDate = startDateMomentObj.clone().add(15, 'minutes'); // set end date to 15 minutes after the start date by default

            const paymentConsent = await resolvePaymentConsent('neighbors_create_order');

            const requestObject = {
                Category: selectedServiceCategoryName,
                ResidentId: selectedResidentId,
                OrderName: orderName,
                Description: description,
                ...(!nowRequest && {
                    ScheduledTime: {
                        startDate: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
                        endDate: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
                    },
                }),
                ...(nowRequest && { RequestedTime: moment().valueOf() }),
                Facility: facilityId,
                RequestChannel: "Web",
                ServiceAddOns: selectedServiceAddOns,
            };
            const Data = {
                ...requestObject,
                OriginalServiceId: String(selectedService._id),
                ...(paymentConsent && { payment: { consent: paymentConsent } }),
            };
            const createdRequestId = await createOrder(FacilityId, Data);
            if (!createdRequestId?.Result?.Content) {
                throw new Error('An error occurred while creating request');
            }
            const stringifiedRequestId = String(createdRequestId.Result.Content);
            // The request itself is already created at this point. Treat the image
            // attach as best-effort and always reset/close on success of the create,
            // rather than leaving the form open for a retry that would create a
            // second, duplicate request.
            let imageWarning = '';
            try {
                if (selectedImage) {
                    await uploadImageFile(stringifiedRequestId);
                    const updateRes = await updateOrder(FacilityId, {
                        _id: stringifiedRequestId,
                        ImageName: selectedImage.name,
                    });
                    if (!updateRes.Result.Content.updated && updateRes.Result.Content.message) { // backend will return updated as false with a message as to why updated is false
                        imageWarning = updateRes.Result.Content.message;
                    }
                } else if (selectedService && selectedService.image && serviceImage) {
                    await copyServiceImageToOrder(stringifiedRequestId);
                    const updateRes = await updateOrder(FacilityId, {
                        _id: stringifiedRequestId,
                        ImageName: selectedService.image,
                    });
                    if (!updateRes.Result.Content.updated && updateRes.Result.Content.message) { // backend will return updated as false with a message as to why updated is false
                        imageWarning = updateRes.Result.Content.message;
                    }
                }
            } catch (imageError) {
                imageWarning = imageError instanceof Error ? imageError.message : 'Failed to attach the image to the request';
            }
            if (imageWarning) {
                sendToast('warn', `Request created, but the image could not be attached: ${imageWarning}`);
            } else {
                sendToast('success', 'Request created successfully');
            }
            resetState();
            refreshHandler();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'An error occurred while creating request');
        } finally {
            setIsFetching(false);
            isSubmittingRef.current = false;
        }
    };

    const uploadImageFile = async (orderId: string) => {
        if (!selectedImage) return;
        const putUrl = await fetchOrderImageUploadUrl(FacilityId, orderId, selectedImage.name);
        const uploadRes = await uploadToSignedUrl(selectedImage, putUrl.Result.Content);
        if (!uploadRes.ok) {
            throw new Error('Failed to upload image');
        }
    };

    const copyServiceImageToOrder = async (orderId: string) => {
        if (!selectedService || !selectedService.image || !serviceImage) return;

        const response = await fetch(serviceImage, { cache: 'no-store' });
        if (!response.ok) {
            throw new Error('Failed to download service image for copy');
        }
        const blob = await response.blob();

        const putUrl = await fetchOrderImageUploadUrl(FacilityId, orderId, selectedService.image);
        const uploadRes = await uploadToSignedUrl(blob, putUrl.Result.Content);
        if (!uploadRes.ok) {
            throw new Error('Failed to upload copied service image');
        }
    };

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const selectedFile = event.target.files[0];
            if (selectedFile) {
                setSelectedImage(selectedFile);
            }
        }
    };

    // Object URLs must be revoked once nothing references them, or each image
    // pick leaks a blob for the life of the tab. Recompute (and revoke the old
    // one) only when the selected file actually changes, not on every render.
    const selectedImagePreviewUrl = useMemo(
        () => (selectedImage ? URL.createObjectURL(selectedImage) : ''),
        [selectedImage],
    );
    useEffect(() => {
        if (!selectedImagePreviewUrl) return;
        return () => URL.revokeObjectURL(selectedImagePreviewUrl);
    }, [selectedImagePreviewUrl]);

    const resetState = () => {
        // setStepForm(1) must come first: this runs after an `await` (outside
        // React's event-handler batching), so each call below re-renders
        // immediately. Clearing selectedServiceId/selectedServiceCategoryName
        // before the step is back to 1 would transiently look like "on step 3
        // with nothing selected" to the wizard's validation effect, firing
        // spurious "not selected" warning toasts right after a successful create.
        setStepForm(1);
        setSelectedServiceId('');
        setSelectedServiceCategoryName('');
        setSelectedServiceAddOns([]);
        setOrderName('');
        setDescription('');
        setSelectedImage(null);
        setStartDate('');
        setNowRequest(true);
    };

    const handleCancel = () => {
        setStepForm(2);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="dept-crumb" onClick={() => setStepForm(2)}>
                <Icon name="arrow left" />
                <span>{selectedService?.name || selectedServiceCategoryName}</span>
            </div>

            <div className="service-summary-card">
                <div className="service-summary-icon">
                    {serviceImage ? (
                        <img src={serviceImage} alt={selectedService?.name} />
                    ) : (
                        <span className="service-summary-icon-missing">No image</span>
                    )}
                </div>
                <div className="service-summary-info">
                    <div className="service-summary-name">{selectedService?.name}</div>
                    {selectedService?.shortDescription && (
                        <div className="service-summary-desc">{selectedService.shortDescription}</div>
                    )}
                    {assetName && (
                        <div className="service-summary-meta">
                            Asset:{' '}
                            {assetUrl ? (
                                <a href={normalizeUrl(assetUrl)} target="_blank" rel="noopener noreferrer">{assetName}</a>
                            ) : assetName}
                        </div>
                    )}
                    {(loadingDocs || serviceDocs.length > 0) && (
                        <div className="service-summary-meta">
                            Attached Docs:{' '}
                            {loadingDocs ? <Icon name="spinner" loading /> : serviceDocs.map((doc) => {
                                const href = getAttachedFileLink(doc);
                                if (!href) return null;
                                return (
                                    <a key={doc._id || doc.Name} href={href} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6 }}>
                                        {doc.Name}
                                    </a>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {cardGateReason && (
                <Message warning className="orderCardGateNotice">
                    <Message.Header>Card Required For Payable Order</Message.Header>
                    <p>{getCardGateMessage(cardGateReason)}</p>
                    <div className="orderCardGateActions">
                        <Button type="button" size="small" primary onClick={() => history.push('/payments')}>
                            {getCardGateActionLabel(cardGateReason)}
                        </Button>
                        <Button type="button" size="small" onClick={handleRetryCardEligibility} loading={cardEligibilityLoading} disabled={cardEligibilityLoading}>
                            Retry
                        </Button>
                    </div>
                </Message>
            )}

            {(serviceAddOns.length > 0 || externalCost > 0) && (
                <div id="addonSection">
                    <div className="addon-section-label">Add-Ons</div>
                    <div className="addon-check-card">
                        {serviceAddOns.map((item) => {
                            const isChecked = item.required || !!selectedServiceAddOns.find((addon) => addon.id === item.id);
                            return (
                                <div
                                    key={item.id}
                                    className={`addon-check-item${isChecked ? ' checked' : ''}`}
                                    onClick={() => !item.required && handleServiceAddons(item.id, !isChecked)}
                                >
                                    <div className="addon-checkbox">
                                        <Checkbox
                                            checked={isChecked}
                                            disabled={item.required}
                                            onChange={(e, { checked }) => handleServiceAddons(item.id, checked as boolean)}
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="addon-check-name">{item.itemName}</div>
                                    <div className="addon-check-cost">${Number(item.itemCost) || 0}</div>
                                </div>
                            );
                        })}
                        {externalCost > 0 && (
                            <div className="addon-check-item">
                                <div className="addon-check-name">External Cost</div>
                                <div className="addon-check-cost">${Number(externalCost).toFixed(2)}</div>
                            </div>
                        )}
                    </div>
                    {paymentRequired && shownChargePreview && shownChargePreview.breakdown ? (
                        <div className="detail-total-row">
                            <ChargeBreakdown
                                breakdown={shownChargePreview.breakdown}
                                note={shownChargePreview.capApplied ? 'Capped at the maximum single charge.' : undefined}
                            />
                        </div>
                    ) : totalCostForOrder > 0 && (
                        <div className="detail-total-row">
                            <span>
                                <b>{paymentRequired ? 'Subtotal' : 'Total'}: ${totalCostForOrder.toFixed(2)}</b>
                                {paymentRequired && (chargePreviewLoading ? ' · calculating tax…' : ' + tax')}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="order-field">
                <label className="order-label" htmlFor="orderNameInput">Order Name</label>
                <input
                    id="orderNameInput"
                    className="order-input"
                    type="text"
                    required
                    placeholder="e.g. Kitchen lightbulb"
                    value={orderName}
                    onChange={(e) => setOrderName(e.target.value)}
                />
            </div>

            <div className="order-field">
                <label className="order-label" htmlFor="orderDescInput">Order Description</label>
                <textarea
                    id="orderDescInput"
                    className="order-textarea"
                    placeholder="Add any details the staff should know..."
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                />
            </div>

            <div className="order-field">
                <label className="order-label">Images</label>
                <input
                    id="createOrderImageInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                />
                <label htmlFor="createOrderImageInput" className="order-image-picker">
                    {selectedImagePreviewUrl ? (
                        <img src={selectedImagePreviewUrl} alt="Selected" />
                    ) : serviceImage ? (
                        // No image chosen yet — preview the service's own image, since that's
                        // what gets copied onto the order at submit time if nothing is picked here.
                        <img src={serviceImage} alt={selectedService?.name} />
                    ) : (
                        <Icon name="image outline" size="big" />
                    )}
                </label>
            </div>

            <div className="order-field">
                <label className="order-label">When</label>
                <div className="schedule-choices">
                    <button type="button" className={`schedule-choice${nowRequest ? ' active' : ''}`} onClick={selectScheduleNow}>
                        <Icon name="clock outline" />
                        Now
                    </button>
                    <button type="button" className={`schedule-choice${!nowRequest ? ' active' : ''}`} onClick={selectScheduleLater}>
                        <Icon name="calendar alternate outline" />
                        Schedule
                    </button>
                </div>
                {!nowRequest && (
                    <DatePicker
                        customInput={<input className="order-input" placeholder="Requested Time *" />}
                        minDate={new Date()}
                        dateFormat="M.d.Y h:mm aa"
                        showTimeSelect
                        timeIntervals={15}
                        selected={startDate ? Date.parse(startDate || new Date().toISOString()) : null}
                        onChange={(date: Date) => {
                            if (!date) return;
                            setStartDate(momentTZ(date).format('YYYY-MM-DDTHH:mm:ss'));
                        }}
                        // The wizard modal clips overflow, and this field sits at the
                        // bottom of the form, so a downward popper is cut off and
                        // cannot be scrolled to (it's absolutely positioned). Always
                        // open above the input, and portal out of the overflow box.
                        portalId="create-order-datepicker-portal"
                        popperPlacement="top-start"
                        popperClassName="create-order-datepicker-popper"
                        popperProps={{ positionFixed: true }}
                        popperModifiers={{
                            flip: { enabled: false },
                            preventOverflow: {
                                enabled: true,
                                escapeWithReference: false,
                                boundariesElement: 'viewport',
                            },
                        }}
                    />
                )}
            </div>

            <div className="wizard-footer">
                <Button type="button" className="wizard-btn-back" onClick={handleCancel}>Back</Button>
                <Button type="submit" className="wizard-btn-next" disabled={!canSubmit}>Create Request</Button>
            </div>
        </form>
    );
};

export default CreateOrderStepThree;
